<script setup lang="ts">
/**
 * Uploads tray (Google Drive style), bottom-right, app-wide: a header with the overall state, collapse and close,
 * and one row per file — bytes uploading with a progress ring, then "Optimizing for players", then Ready. Optimizing
 * files are already in Content but can't be published until Ready.
 */
import { confirmDialog } from '../stores/confirm';
import { useI18n } from 'vue-i18n';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { busy, clearUploads, dismissUpload, type UploadEntry, uploading, uploads } from '../stores/uploads';
import { formatBytes } from '../utils/format';

const { t } = useI18n();

const collapsed = ref(false);
const counts = computed(() => ({
  up: uploads.filter((u) => u.stage === 'uploading').length,
  opt: uploads.filter((u) => u.stage === 'optimizing').length,
  ready: uploads.filter((u) => u.stage === 'ready').length,
  err: uploads.filter((u) => u.stage === 'error').length,
}));
const title = computed(() => {
  const c = counts.value;
  if (c.up) return t('ui.uploadingN', c.up);
  if (c.opt) return t('ui.optimizingN', c.opt);
  if (c.err && !c.ready) return t('ui.failedN', c.err);
  return t('ui.completeN', c.ready) + (c.err ? t('ui.andFailed', { n: c.err }) : '');
});
/** 0..1 for the ring: bytes while uploading, the worker's progress while optimizing. */
const fraction = (u: UploadEntry) => (u.stage === 'uploading' ? u.sent : u.stage === 'optimizing' ? (u.optimize ?? 0) / 100 : 1);
const detail = (u: UploadEntry) =>
  u.stage === 'uploading'
    ? t('ui.sentOf', { sent: formatBytes(Math.round(u.sent * u.size)), size: formatBytes(u.size) })
    : u.stage === 'optimizing'
      ? u.optimize === null
        ? t('ui.waitOptimize')
        : t('ui.optimizingPct', { pct: u.optimize })
      : u.stage === 'ready'
        ? t('ui.readyToPublish')
        : u.stage === 'cancelled'
          ? t('ui.cancelled')
          : (u.error ?? t('ui.failed'));
const icon = (name: string) => (/\.(mp4|mov|mkv|webm)$/i.test(name) ? '🎬' : /\.(mp3|m4a|aac|wav|ogg|flac)$/i.test(name) ? '🎵' : '🖼️');
const R = 9;
const C = 2 * Math.PI * R;
/**
 * All done and nothing failed: the tray closes by itself a few seconds later (as the sync tray does), unless the
 * pointer is over it. A failed or cancelled file keeps it open until closed, so nobody misses it.
 */
const AUTO_CLOSE_MS = 4_000;
const hovered = ref(false);
const allReady = computed(() => uploads.length > 0 && uploads.every((u) => u.stage === 'ready'));
let closeTimer: ReturnType<typeof setTimeout> | undefined;
watch(
  [allReady, hovered, () => uploads.length],
  ([ready, over]) => {
    clearTimeout(closeTimer);
    if (ready && !over) closeTimer = setTimeout(clearUploads, AUTO_CLOSE_MS);
  },
);
onBeforeUnmount(() => clearTimeout(closeTimer));
async function close(): Promise<void> {
  if (uploading.value && !await confirmDialog(t('ui.cancelUnfinished'))) return;
  clearUploads();
}
</script>

<template>
  <section
    v-if="uploads.length"
    class="w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
    :aria-label="t('ui.uploads')"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
    @focusin="hovered = true"
    @focusout="hovered = false"
  >
    <header class="flex items-center gap-2 bg-slate-900 px-4 py-2.5 text-white dark:bg-slate-800">
      <h2 class="min-w-0 flex-1 truncate text-sm font-medium" aria-live="polite">{{ title }}</h2>
      <button type="button" class="rounded p-1 hover:bg-white/10" :aria-label="collapsed ? t('ui.expandUploads') : t('ui.collapseUploads')" :aria-expanded="!collapsed" @click="collapsed = !collapsed">
        <svg viewBox="0 0 20 20" class="h-4 w-4 transition" :class="collapsed ? 'rotate-180' : ''" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M5 8l5 5 5-5" /></svg>
      </button>
      <button type="button" class="rounded p-1 hover:bg-white/10" :aria-label="t('ui.closeUploads')" @click="close">
        <svg viewBox="0 0 20 20" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" /></svg>
      </button>
    </header>
    <p v-if="!collapsed && busy && counts.opt" class="border-b border-slate-100 bg-amber-50 px-4 py-1.5 text-xs text-amber-900 dark:border-slate-800 dark:bg-amber-950/40 dark:text-amber-200">
      {{ t('ui.uploadsHint') }}
    </p>
    <ul v-if="!collapsed" class="max-h-72 divide-y divide-slate-100 overflow-y-auto dark:divide-slate-800">
      <li v-for="u in uploads" :key="u.key" class="group flex items-center gap-3 px-4 py-2.5">
        <span class="text-lg" aria-hidden="true">{{ icon(u.name) }}</span>
        <div class="min-w-0 flex-1">
          <p class="truncate text-sm" :title="u.name">{{ u.name }}</p>
          <p class="truncate text-xs" :class="u.stage === 'error' ? 'text-red-600 dark:text-red-400' : u.stage === 'ready' ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-500'">{{ detail(u) }}</p>
        </div>
        <!-- Ring while working; ✓ ready; ! failed. Cancel replaces the ring on hover while uploading. -->
        <button
          v-if="u.stage === 'uploading'"
          type="button"
          class="hidden h-6 w-6 items-center justify-center rounded-full text-slate-500 group-hover:flex hover:bg-slate-100 dark:hover:bg-slate-800"
          :aria-label="t('ui.cancelName', { name: u.name })"
          @click="u.abort()"
        >
          <svg viewBox="0 0 20 20" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" /></svg>
        </button>
        <svg
          v-if="u.stage === 'uploading' || u.stage === 'optimizing'"
          viewBox="0 0 24 24"
          class="h-6 w-6 shrink-0 -rotate-90"
          :class="u.stage === 'uploading' ? 'group-hover:hidden' : ''"
          role="progressbar"
          :aria-valuenow="Math.round(fraction(u) * 100)"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-label="u.stage === 'uploading' ? t('ui.uploadingName', { name: u.name }) : t('ui.optimizingName', { name: u.name })"
        >
          <circle cx="12" cy="12" :r="R" fill="none" stroke-width="3" class="stroke-slate-200 dark:stroke-slate-700" />
          <circle
            cx="12"
            cy="12"
            :r="R"
            fill="none"
            stroke-width="3"
            stroke-linecap="round"
            :class="u.stage === 'uploading' ? 'stroke-indigo-600' : 'stroke-amber-500'"
            :stroke-dasharray="C"
            :stroke-dashoffset="C * (1 - fraction(u))"
            style="transition: stroke-dashoffset 0.3s"
          />
          <!-- waiting for the worker: a small spinning arc -->
          <circle v-if="u.stage === 'optimizing' && u.optimize === null" cx="12" cy="12" :r="R" fill="none" stroke-width="3" stroke-linecap="round" class="origin-center animate-spin stroke-amber-500" :stroke-dasharray="`${C / 4} ${C}`" />
        </svg>
        <span v-else-if="u.stage === 'ready'" class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white" :aria-label="t('ui.ready')">
          <svg viewBox="0 0 20 20" class="h-3.5 w-3.5" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 10.5l3.5 3.5L15 7" /></svg>
        </span>
        <button v-else type="button" class="text-xs text-slate-500 hover:underline" @click="dismissUpload(u.key)">{{ t('ui.dismiss') }}</button>
      </li>
    </ul>
  </section>
</template>
